import * as React from 'react';
import { action, observable } from 'mobx';
import { bind, inject } from '../../../utils/decorators';
import { UiStore } from '../../../stores/UiStore';
import { Panel, FormGroup, Col, FormControl, ControlLabel, Form, Button, Modal, InputGroup } from 'react-bootstrap';
import { observer } from 'mobx-react';
import { ApiStore } from '../../../stores/ApiStore';
import * as ApiTypes from '../../../models/ApiTypes';
import * as ApiFactory from '../../../models/ApiTypeFactory';
import FaIcon from '../../site/FaIcon';
import AuftragAdresseUebernehmen from './AuftragAdresseUebernehmen';

interface IAuftragAdresseAendernHeaderProps {
  caption: string;
  showModal: () => void;
}

/** Header für das Panel zur Adressänderungen mit Funktionen je nach Anwenderstufe  */
export class AuftragAdresseAendernHeader extends React.Component<IAuftragAdresseAendernHeaderProps> {

  @inject(UiStore) uiStore: UiStore;

  render() {
    const portalAdmin = this.uiStore.userProfile && this.uiStore.userProfile.portaladmin;
    return (
      <div>
        <div className="panel-title pull-left">
          {this.props.caption}
        </div>
        <div className="panel-title pull-right">
            <Button bsStyle="default" onClick={this.props.showModal}><FaIcon icon="address-book-o" /> {portalAdmin ? 'Aus Adresse' : 'Aus Untersuchungsstelle'}</Button>
        </div>
        <div className="clearfix"/>
      </div>
    );
  }

}

interface IAuftragAdresseAendernProps {
  auftrag: ApiTypes.IAuftrag;
  adressArt: ApiTypes.AdressArt;
  caption: string;
  onAuftragNeuladen: () => void;
}

/** Komponente zum nachträglichen Ändern einer im Auftrag erfassten Adresse */
@observer
export default class AuftragAdresseAendern extends React.Component<IAuftragAdresseAendernProps, {}> {

  @inject(UiStore) uiStore: UiStore;
  @inject(ApiStore) apiStore: ApiStore;

  @observable adresse: ApiTypes.IAdresse = ApiFactory.leereAdresse();

  @observable modalVisible: boolean = false;

  @bind @action showModal() {
    this.modalVisible = true;
  }

  @bind @action hideModal() {
    this.modalVisible = false;
  }

  @bind @action setAdresse(adresse: ApiTypes.IAdresse) {
    this.adresse = adresse;
  }

  @bind async uebernehmeAdresse(adresse: ApiTypes.IAdresse) {
    this.setAdresse(adresse);
    this.hideModal();
  }

  constructor(props: IAuftragAdresseAendernProps) {
    super(props);
    // Daten aus Auftrag übernehmen
    let { auftrag } = this.props;
    if (this.props.adressArt === ApiTypes.AdressArt.Auftraggeber) {
      this.adresse.name = auftrag.auftraggeber_name1;
      if (auftrag.auftraggeber_name2) {
        this.adresse.zusatz = auftrag.auftraggeber_name2;
      }
      this.adresse.strasse = auftrag.auftraggeber_strasse;
      this.adresse.plz = auftrag.auftraggeber_plz;
      this.adresse.ort = auftrag.auftraggeber_ort;
      if (auftrag.auftraggeber_tel) {
        this.adresse.tel1 = auftrag.auftraggeber_tel;
      }
      if (auftrag.auftraggeber_email) {
        this.adresse.email = auftrag.auftraggeber_email;
      }
      if (auftrag.auftraggeber_email2) {
        this.adresse.email2 = auftrag.auftraggeber_email2;
      }
      if (auftrag.auftraggeber_adressid) {
        this.adresse.adressid = auftrag.auftraggeber_adressid;
      }
    }
    if (this.props.adressArt === ApiTypes.AdressArt.Beauftragter) {
      if (auftrag.beauftragter_name1) {
        this.adresse.name = auftrag.beauftragter_name1;
      }
      if (auftrag.beauftragter_name2) {
        this.adresse.zusatz = auftrag.beauftragter_name2;
      }
      if (auftrag.beauftragter_strasse) {
        this.adresse.strasse = auftrag.beauftragter_strasse;
      }
      if (auftrag.beauftragter_plz) {
        this.adresse.plz = auftrag.beauftragter_plz;
      }
      if (auftrag.beauftragter_ort) {
        this.adresse.ort = auftrag.beauftragter_ort;
      }
      if (auftrag.beauftragter_tel) {
        this.adresse.tel1 = auftrag.beauftragter_tel;
      }
      if (auftrag.beauftragter_email) {
        this.adresse.email = auftrag.beauftragter_email;
      }
      if (auftrag.beauftragter_adressid) {
        this.adresse.adressid = auftrag.beauftragter_adressid;
      }
      // Eine Änderung der Untersuchungsstellenkennung macht nur im Ort der Kalibrierung Sinn
      if (auftrag.untersuchungsstellenkennung && auftrag.beauftragter_adressid) {
        this.adresse.untersuchungsstellenkennung = auftrag.untersuchungsstellenkennung;
      }
    }
  }

  validate() {
    return (this.adresse.name.trim() !== '');
  }

  @bind async handleSubmit(event: React.FormEvent<FormControl>) {
    event.preventDefault();
    const response = await this.apiStore.auftragAdresseAendern(this.props.auftrag.anr, this.props.adressArt, this.adresse);
    if (response.ok) {
      await this.props.onAuftragNeuladen();
      this.uiStore.navigateTo(`/auftrag/${this.props.auftrag.anr}`);
    }
  }

  @bind @action handleNameChanged(event: React.FormEvent<FormControl>) {
    this.adresse.name = (event.target as HTMLInputElement).value;
  }

  @bind @action handleZusatzChanged(event: React.FormEvent<FormControl>) {
    this.adresse.zusatz = (event.target as HTMLInputElement).value;
  }

  @bind @action handleStrasseChanged(event: React.FormEvent<FormControl>) {
    this.adresse.strasse = (event.target as HTMLInputElement).value;
  }

  @bind @action handlePlzChanged(event: React.FormEvent<FormControl>) {
    this.adresse.plz = (event.target as HTMLInputElement).value;
  }

  @bind @action handleOrtChanged(event: React.FormEvent<FormControl>) {
    this.adresse.ort = (event.target as HTMLInputElement).value;
  }

  @bind @action handleTelefonChanged(event: React.FormEvent<FormControl>) {
    this.adresse.tel1 = (event.target as HTMLInputElement).value;
  }

  @bind @action handleEMailChanged(event: React.FormEvent<FormControl>) {
    this.adresse.email = (event.target as HTMLInputElement).value;
  }

  @bind @action handleEMail2Changed(event: React.FormEvent<FormControl>) {
    this.adresse.email2 = (event.target as HTMLInputElement).value;
  }

  @bind @action handleUstLoeschen(event: React.FormEvent<FormControl>) {
    this.adresse.untersuchungsstellenkennung = '';
    this.adresse.adressid = undefined;
  }

  render() {
    const portalAdmin = this.uiStore.userProfile && this.uiStore.userProfile.portaladmin;
    return (
      <Form horizontal={true} onSubmit={this.handleSubmit}>
        <Panel className="panel-header-buttoned"
          header={<AuftragAdresseAendernHeader caption={this.props.caption} showModal={this.showModal}/>}
          footer={
            <div className="text-right">
              <Button onClick={() => this.uiStore.navigateBack()}>Abbrechen</Button>
              <Button bsStyle="primary" type="submit" disabled={!this.validate()}>Änderungen speichern</Button>
            </div>
          }
        >
          <FormGroup validationState={this.validate() ? undefined : 'error'} controlId="Name">
            <Col componentClass={ControlLabel} sm={3}>Name</Col>
            <Col sm={9}>
              <FormControl type="text" label="Name" value={this.adresse.name || ''} onChange={this.handleNameChanged} />
            </Col>
          </FormGroup>

          <FormGroup validationState={this.validate() ? undefined : 'error'} controlId="Name">
            <Col componentClass={ControlLabel} sm={3}>Zusatz</Col>
            <Col sm={9}>
              <FormControl type="text" label="Zusatz" value={this.adresse.zusatz || ''} onChange={this.handleZusatzChanged} />
            </Col>
          </FormGroup>

          <FormGroup controlId="Strasse">
            <Col componentClass={ControlLabel} sm={3}>Straße</Col>
            <Col sm={9}>
              <FormControl type="text" label="Strasse" value={this.adresse.strasse || ''} onChange={this.handleStrasseChanged} />
            </Col>
          </FormGroup>

          <FormGroup controlId="PLZ">
            <Col componentClass={ControlLabel} sm={3}>PLZ</Col>
            <Col sm={9}>
              <FormControl type="text" label="PLZ" value={this.adresse.plz || ''} onChange={this.handlePlzChanged} />
            </Col>
          </FormGroup>

          <FormGroup controlId="Ort">
            <Col componentClass={ControlLabel} sm={3}>Ort</Col>
            <Col sm={9}>
              <FormControl type="text" label="Ort" value={this.adresse.ort || ''} onChange={this.handleOrtChanged} />
            </Col>
          </FormGroup>

          <FormGroup controlId="Telefonnummer">
            <Col componentClass={ControlLabel} sm={3}>Telefonnummer</Col>
            <Col sm={9}>
              <FormControl type="text" label="Telefonnummer" placeholder="Optional" value={this.adresse.tel1 || ''} onChange={this.handleTelefonChanged} />
            </Col>
          </FormGroup>

          <FormGroup controlId="Email">
            <Col componentClass={ControlLabel} sm={3}>E-Mail</Col>
            <Col sm={9}>
              <FormControl type="text" label="Email" placeholder="Optional" value={this.adresse.email || ''} onChange={this.handleEMailChanged} />
            </Col>
          </FormGroup>

          {/* Erfassen der Buchhaltungs E-Mail macht nur beim Auftraggeber Sinn, weil die Rechnung an ihn geht */}
          {(this.props.adressArt === ApiTypes.AdressArt.Auftraggeber) &&
          <FormGroup controlId="EmailBuchhaltung">
            <Col componentClass={ControlLabel} sm={3}>E-Mail Buchhaltung</Col>
            <Col sm={9}>
              <FormControl type="text" label="EmailBuchhaltung" placeholder="Optional" value={this.adresse.email2 || ''} onChange={this.handleEMail2Changed} />
            </Col>
          </FormGroup>
          }

          {/* Eine Änderung der Untersuchungsstellenkennung macht nur im Ort der Kalibrierung Sinn */}
          {(this.props.adressArt === ApiTypes.AdressArt.Beauftragter) && this.adresse.untersuchungsstellenkennung &&
          <FormGroup controlId="zugeordneteust">
            <Col componentClass={ControlLabel} sm={3}>Zugeordnete USt.</Col>
            <Col sm={9}>
              <InputGroup>
                <FormControl type="text" label="Untersuchungsstellenkennung" placeholder="Optional" value={this.adresse.untersuchungsstellenkennung} readOnly={true}/>
                <InputGroup.Button>
                    <Button
                      bsStyle="default"
                      className="btn-group"
                      onClick={this.handleUstLoeschen}>
                      <FaIcon icon="trash" />
                    </Button>
                </InputGroup.Button>
              </InputGroup>
            </Col>
          </FormGroup>
        }

        </Panel>

        {/* Die Funktion zur Übernahme der Adressdaten wird modal angezeigt */}

        <Modal show={this.modalVisible} onHide={this.hideModal} className="modal-vertical-centered" backdrop={'static'}>
          <Modal.Header closeButton={true}>
            <Modal.Title>Adresse übernehmen</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {portalAdmin ?
              <div>Adressdaten können entweder über die Kennung einer ÜO oder über eine Adress-ID in den Auftrag übernommen werden.</div>
            :
              <div>Adressdaten können aus der Untersuchungsstellenkennung einer ÜO übernommen werden. Der Auftrag wird dann dieser Untersuchungsstelle zugeordnet.</div>
            }
            <AuftragAdresseUebernehmen onUebernehme={this.uebernehmeAdresse} adressArt={ApiTypes.AdressArt.Untersuchungsstelle}/>
            {/* Nur interne Mitarbeiter dürfen auch aus AdressIDs übernehmen */}
            {portalAdmin && <AuftragAdresseUebernehmen onUebernehme={this.uebernehmeAdresse} adressArt={ApiTypes.AdressArt.Auftraggeber}/>}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={this.hideModal} bsStyle="default">Abbrechen</Button>
          </Modal.Footer>
        </Modal>

      </Form>);
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/auftraege/adressen/AuftragAdresseAendern.tsx